import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plane, Plus, Trash2, Upload, Paperclip, IndianRupee, Receipt, Check, X, Eye, ClipboardList } from "lucide-react";

const CATEGORIES = ["Flight", "Hotel", "Cab", "Meal", "Fuel", "Parking", "Other"];
const PURPOSES = ["Client Visit", "Training", "Conference", "Internal Meeting", "Other"];

const STATUS_STYLE = {
  "Draft": "bg-slate-100 text-slate-700",
  "Pending Manager": "bg-amber-50 text-amber-700",
  "Pending HR": "bg-blue-50 text-blue-700",
  "Reimbursed": "bg-emerald-50 text-emerald-700",
  "Rejected": "bg-red-50 text-red-700",
  "Returned": "bg-orange-50 text-orange-700",
};

const rupee = (n) => `₹ ${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
const fmtDate = (d) => { try { return new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return d; } };

function StatusBadge({ s }) {
  return <Badge className={`${STATUS_STYLE[s] || "bg-slate-100 text-slate-700"} border-0 hover:${STATUS_STYLE[s]}`}>{s}</Badge>;
}

export default function Expenses() {
  const { user } = useAuth();
  const isHR = user?.role === "admin" || user?.role === "hr";
  const [tab, setTab] = useState("mine");
  const [mine, setMine] = useState([]);
  const [mgrQueue, setMgrQueue] = useState([]);
  const [hrQueue, setHrQueue] = useState([]);
  const [summary, setSummary] = useState({});
  const [openCreate, setOpenCreate] = useState(false);
  const [openDetail, setOpenDetail] = useState(null);

  const reload = async () => {
    const p = [api.get("/expenses/mine"), api.get("/expenses/pending-manager"), api.get("/expenses/summary/dashboard")];
    if (isHR) p.push(api.get("/expenses/pending-hr"));
    const results = await Promise.all(p);
    setMine(results[0].data);
    setMgrQueue(results[1].data);
    setSummary(results[2].data);
    if (isHR) setHrQueue(results[3].data);
  };
  useEffect(() => { reload(); }, []);

  const openExpense = async (id) => {
    const { data } = await api.get(`/expenses/${id}`);
    setOpenDetail(data);
  };

  return (
    <div className="space-y-6" data-testid="expenses-root">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-slate-500">
            <Plane className="h-3.5 w-3.5" /> Travel & Expense
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mt-1">Expense Claims</h1>
          <p className="text-sm text-slate-500 mt-1">Submit travel expenses with receipts. Manager approves, HR reimburses.</p>
        </div>
        <Button className="bg-[#DC2626] hover:bg-[#B91C1C]" onClick={() => setOpenCreate(true)} data-testid="new-expense-btn">
          <Plus className="h-4 w-4 mr-1.5" /> New claim
        </Button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Stat title="My claims" value={summary.my_total ?? 0} />
        <Stat title="My pending" value={summary.my_pending ?? 0} accent="amber" />
        <Stat title="My reimbursed" value={rupee(summary.my_reimbursed_amount)} />
        {isHR
          ? <Stat title="HR queue" value={summary.hr_queue ?? 0} accent="blue" />
          : <Stat title="Awaiting me (manager)" value={summary.team_pending ?? 0} accent="orange" />}
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="mine" data-testid="tab-mine">My claims ({mine.length})</TabsTrigger>
          <TabsTrigger value="team" data-testid="tab-team">Team queue ({mgrQueue.length})</TabsTrigger>
          {isHR && <TabsTrigger value="hr" data-testid="tab-hr">HR queue ({hrQueue.length})</TabsTrigger>}
        </TabsList>
        <TabsContent value="mine"><ClaimTable rows={mine} onOpen={openExpense} /></TabsContent>
        <TabsContent value="team"><ClaimTable rows={mgrQueue} onOpen={openExpense} /></TabsContent>
        {isHR && <TabsContent value="hr"><ClaimTable rows={hrQueue} onOpen={openExpense} /></TabsContent>}
      </Tabs>

      <CreateClaimDialog open={openCreate} onClose={() => setOpenCreate(false)} onCreated={async (id) => { setOpenCreate(false); await reload(); await openExpense(id); }} />
      {openDetail && (
        <ExpenseDetailDialog
          expense={openDetail}
          currentUser={user}
          onClose={() => setOpenDetail(null)}
          onChanged={async () => { await reload(); await openExpense(openDetail.id); }}
          onFullClose={async () => { setOpenDetail(null); await reload(); }}
        />
      )}
    </div>
  );
}

function Stat({ title, value, accent = "slate" }) {
  const c = {
    slate: "text-slate-900", amber: "text-amber-700", blue: "text-blue-700", orange: "text-orange-700",
  }[accent];
  return (
    <Card className="p-4 rounded-xl border-slate-200/60">
      <div className="text-[10px] uppercase tracking-widest font-semibold text-slate-500">{title}</div>
      <div className={`mt-1 text-2xl font-bold ${c}`}>{value}</div>
    </Card>
  );
}

function ClaimTable({ rows, onOpen }) {
  if (!rows.length) return <div className="text-sm text-slate-500 py-8 text-center">No claims here yet.</div>;
  return (
    <Card className="rounded-xl border-slate-200/60 overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
            <tr>
              <th className="text-left px-4 py-3">Claim #</th>
              <th className="text-left px-4 py-3">Title</th>
              <th className="text-left px-4 py-3">Employee</th>
              <th className="text-left px-4 py-3">Trip</th>
              <th className="text-right px-4 py-3">Amount</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {rows.map(r => (
              <tr key={r.id} className="hover:bg-slate-50/50" data-testid={`claim-row-${r.expense_no}`}>
                <td className="px-4 py-3 font-mono text-xs text-slate-700">{r.expense_no}</td>
                <td className="px-4 py-3 font-medium text-slate-900">{r.title}</td>
                <td className="px-4 py-3 text-slate-600">{r.employee_name}</td>
                <td className="px-4 py-3 text-xs text-slate-500">{r.from_city} → {r.to_city}<br /><span className="text-[10px]">{fmtDate(r.from_date)} – {fmtDate(r.to_date)}</span></td>
                <td className="px-4 py-3 text-right font-semibold">{rupee(r.total)}</td>
                <td className="px-4 py-3"><StatusBadge s={r.status} /></td>
                <td className="px-4 py-3 text-right">
                  <Button size="sm" variant="outline" onClick={() => onOpen(r.id)} data-testid={`open-${r.expense_no}`}>
                    <Eye className="h-3.5 w-3.5 mr-1" /> Open
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function CreateClaimDialog({ open, onClose, onCreated }) {
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    title: "", purpose: "Client Visit", customer: "",
    from_date: "", to_date: "", from_city: "Bengaluru", to_city: "",
    advance_taken: 0, notes: "",
  });
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/expenses", form);
      toast.success(`Claim ${data.expense_no} created — add items now`);
      onCreated(data.id);
      setForm({ ...form, title: "", customer: "", to_city: "", notes: "", advance_taken: 0 });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally { setBusy(false); }
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target?.value ?? e });
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg">
        <DialogHeader><DialogTitle>New expense claim</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid="create-claim-form">
          <div>
            <Label>Title</Label>
            <Input value={form.title} onChange={set("title")} required placeholder="e.g. Mumbai client visit" data-testid="claim-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Purpose</Label>
              <Select value={form.purpose} onValueChange={(v) => setForm({ ...form, purpose: v })}>
                <SelectTrigger data-testid="claim-purpose"><SelectValue /></SelectTrigger>
                <SelectContent>{PURPOSES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Customer / Event</Label>
              <Input value={form.customer} onChange={set("customer")} placeholder="Optional" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>From date</Label><Input type="date" value={form.from_date} onChange={set("from_date")} required /></div>
            <div><Label>To date</Label><Input type="date" value={form.to_date} onChange={set("to_date")} required /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>From city</Label><Input value={form.from_city} onChange={set("from_city")} required /></div>
            <div><Label>To city</Label><Input value={form.to_city} onChange={set("to_city")} required placeholder="Mumbai" /></div>
          </div>
          <div>
            <Label>Advance taken (₹)</Label>
            <Input type="number" min={0} value={form.advance_taken} onChange={(e) => setForm({ ...form, advance_taken: Number(e.target.value) })} />
          </div>
          <div>
            <Label>Notes</Label>
            <Textarea value={form.notes} onChange={set("notes")} rows={2} />
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={busy} className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="claim-submit">Create claim</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ExpenseDetailDialog({ expense, currentUser, onClose, onChanged, onFullClose }) {
  const exp = expense;
  const isOwner = exp.employee_id === currentUser?.employee_id;
  const isMgr = exp.manager === currentUser?.name;
  const isHR = currentUser?.role === "admin" || currentUser?.role === "hr";
  const editable = ["Draft", "Returned"].includes(exp.status) && (isOwner || isHR);
  const canDecideMgr = exp.status === "Pending Manager" && (isMgr || currentUser?.role === "admin");
  const canDecideHR = exp.status === "Pending HR" && isHR;
  const canSubmit = editable && exp.items.length > 0;

  return (
    <Dialog open={true} onOpenChange={onFullClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            {exp.expense_no} · {exp.title}
            <StatusBadge s={exp.status} />
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <TripSummary exp={exp} />
          <ItemsSection exp={exp} editable={editable} onChanged={onChanged} />
          <DecisionHistory exp={exp} />

          <div className="flex flex-wrap gap-2 justify-end border-t pt-4">
            {canSubmit && <SubmitButton exp={exp} onChanged={onChanged} />}
            {canDecideMgr && <ManagerDecideButtons exp={exp} onChanged={onChanged} />}
            {canDecideHR && <HRDecideButtons exp={exp} onChanged={onChanged} />}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function TripSummary({ exp }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
      <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Employee</div><div className="font-medium">{exp.employee_name}</div></div>
      <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Purpose</div><div className="font-medium">{exp.purpose}</div></div>
      <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Trip</div><div className="font-medium">{exp.from_city} → {exp.to_city}</div></div>
      <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Dates</div><div className="font-medium">{fmtDate(exp.from_date)} – {fmtDate(exp.to_date)}</div></div>
      <div className="col-span-2"><div className="text-[10px] uppercase tracking-wider text-slate-500">Customer</div><div className="font-medium">{exp.customer || "—"}</div></div>
      <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Advance taken</div><div className="font-medium">{rupee(exp.advance_taken)}</div></div>
      <div><div className="text-[10px] uppercase tracking-wider text-slate-500">Claim total</div><div className="font-bold text-[#DC2626]">{rupee(exp.total)}</div></div>
      {exp.notes && <div className="col-span-full text-xs text-slate-600 border-l-4 border-slate-200 pl-3 italic">{exp.notes}</div>}
    </div>
  );
}

function ItemsSection({ exp, editable, onChanged }) {
  const fileRefs = useRef({});
  const [adding, setAdding] = useState(false);
  const [newItem, setNewItem] = useState({ category: "Flight", date: exp.from_date, amount: 0, vendor: "", description: "" });

  const addItem = async () => {
    if (!newItem.amount || newItem.amount <= 0) return toast.error("Enter an amount");
    try {
      await api.post(`/expenses/${exp.id}/items`, newItem);
      toast.success("Item added");
      setAdding(false);
      setNewItem({ category: "Flight", date: exp.from_date, amount: 0, vendor: "", description: "" });
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  const del = async (id) => {
    try { await api.delete(`/expenses/${exp.id}/items/${id}`); toast.success("Removed"); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  const upload = async (item, file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) return toast.error("Max 8 MB");
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/expenses/${exp.id}/items/${item.id}/receipt`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Receipt attached");
      onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <Card className="p-4 rounded-xl border-slate-200/60">
      <div className="flex items-center justify-between mb-3">
        <div className="font-semibold text-slate-900 flex items-center gap-2"><Receipt className="h-4 w-4 text-[#DC2626]" /> Line items ({exp.items.length})</div>
        {editable && !adding && <Button size="sm" variant="outline" onClick={() => setAdding(true)} data-testid="add-item-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Add item</Button>}
      </div>

      {adding && (
        <div className="mb-4 p-3 rounded-lg border border-dashed border-slate-300 bg-slate-50/50 grid grid-cols-1 md:grid-cols-6 gap-2 items-end" data-testid="new-item-row">
          <div>
            <Label className="text-xs">Category</Label>
            <Select value={newItem.category} onValueChange={(v) => setNewItem({ ...newItem, category: v })}>
              <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
              <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Date</Label><Input type="date" className="h-9" value={newItem.date} onChange={(e) => setNewItem({ ...newItem, date: e.target.value })} /></div>
          <div><Label className="text-xs">Amount (₹)</Label><Input type="number" className="h-9" value={newItem.amount} onChange={(e) => setNewItem({ ...newItem, amount: Number(e.target.value) })} data-testid="item-amount" /></div>
          <div><Label className="text-xs">Vendor</Label><Input className="h-9" value={newItem.vendor} onChange={(e) => setNewItem({ ...newItem, vendor: e.target.value })} placeholder="IndiGo, Marriott…" /></div>
          <div className="md:col-span-1"><Label className="text-xs">Note</Label><Input className="h-9" value={newItem.description} onChange={(e) => setNewItem({ ...newItem, description: e.target.value })} /></div>
          <div className="flex gap-2">
            <Button size="sm" onClick={addItem} className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="save-item-btn">Add</Button>
            <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
          </div>
        </div>
      )}

      {exp.items.length === 0 ? (
        <div className="text-sm text-slate-500 text-center py-6">No items yet. {editable && "Click 'Add item' to record expenses."}</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-[11px] uppercase text-slate-500 tracking-wider">
              <tr className="border-b"><th className="text-left py-2">Cat</th><th className="text-left py-2">Date</th><th className="text-left py-2">Vendor</th><th className="text-right py-2">Amount</th><th className="text-left py-2 pl-4">Receipt</th><th></th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {exp.items.map(it => (
                <tr key={it.id}>
                  <td className="py-2"><Badge className="bg-slate-100 text-slate-700 border-0 hover:bg-slate-100">{it.category}</Badge></td>
                  <td className="py-2 text-slate-600">{fmtDate(it.date)}</td>
                  <td className="py-2">{it.vendor || "—"}{it.description && <div className="text-[11px] text-slate-500">{it.description}</div>}</td>
                  <td className="py-2 text-right font-medium">{rupee(it.amount)}</td>
                  <td className="py-2 pl-4">
                    {it.receipt_stored_name ? (
                      <a
                        href={`${api.defaults.baseURL}/expenses/${exp.id}/items/${it.id}/receipt`}
                        target="_blank" rel="noreferrer"
                        className="text-xs text-[#DC2626] inline-flex items-center gap-1 hover:underline"
                      >
                        <Paperclip className="h-3 w-3" /> {it.receipt_filename || "receipt"}
                      </a>
                    ) : editable ? (
                      <>
                        <input
                          ref={el => fileRefs.current[it.id] = el}
                          type="file" accept="image/*,application/pdf" className="hidden"
                          onChange={(e) => upload(it, e.target.files?.[0])}
                          data-testid={`upload-${it.id}`}
                        />
                        <Button size="sm" variant="outline" onClick={() => fileRefs.current[it.id]?.click()}>
                          <Upload className="h-3 w-3 mr-1" /> Upload
                        </Button>
                      </>
                    ) : <span className="text-xs text-slate-400">—</span>}
                  </td>
                  <td className="py-2 text-right">
                    {editable && <Button size="sm" variant="ghost" className="text-red-600" onClick={() => del(it.id)}><Trash2 className="h-3.5 w-3.5" /></Button>}
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 border-slate-300 font-semibold">
                <td colSpan={3} className="py-3 text-right pr-2">Total</td>
                <td className="py-3 text-right text-[#DC2626]">{rupee(exp.total)}</td>
                <td colSpan={2}></td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function DecisionHistory({ exp }) {
  if (!exp.manager_decision && !exp.hr_decision) return null;
  return (
    <Card className="p-4 rounded-xl border-slate-200/60 space-y-3 text-sm">
      <div className="font-semibold text-slate-900 flex items-center gap-2"><ClipboardList className="h-4 w-4 text-slate-500" /> Approval trail</div>
      {exp.manager_decision && (
        <div className="border-l-4 border-slate-200 pl-3">
          <div className="text-xs text-slate-500">{fmtDate(exp.manager_decided_at)} · Manager · <span className="font-medium">{exp.manager_decided_by}</span></div>
          <div><StatusBadge s={exp.manager_decision === "Approved" ? "Approved" : "Rejected"} /> {exp.manager_remarks && <span className="text-slate-600">— {exp.manager_remarks}</span>}</div>
        </div>
      )}
      {exp.hr_decision && (
        <div className="border-l-4 border-emerald-200 pl-3">
          <div className="text-xs text-slate-500">{fmtDate(exp.hr_decided_at)} · HR · <span className="font-medium">{exp.hr_decided_by}</span></div>
          <div><StatusBadge s={exp.hr_decision} /> {exp.utr_number && <span className="ml-2 text-xs font-mono">UTR: {exp.utr_number}</span>} {exp.hr_remarks && <span className="text-slate-600"> — {exp.hr_remarks}</span>}</div>
        </div>
      )}
    </Card>
  );
}

function SubmitButton({ exp, onChanged }) {
  const submit = async () => {
    try { await api.post(`/expenses/${exp.id}/submit`); toast.success("Submitted for manager approval"); onChanged(); }
    catch (e) { toast.error(formatApiError(e)); }
  };
  return <Button onClick={submit} className="bg-[#DC2626] hover:bg-[#B91C1C]" data-testid="submit-claim-btn">Submit for approval</Button>;
}

function ManagerDecideButtons({ exp, onChanged }) {
  const [remarks, setRemarks] = useState("");
  const [open, setOpen] = useState(null);
  const decide = async () => {
    try {
      await api.put(`/expenses/${exp.id}/manager-decision`, { decision: open, remarks });
      toast.success(open === "Approved" ? "Approved — forwarded to HR" : "Rejected");
      setOpen(null); onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <>
      <Button variant="outline" className="text-red-600 border-red-200" onClick={() => setOpen("Rejected")} data-testid="mgr-reject-btn"><X className="h-4 w-4 mr-1" /> Reject</Button>
      <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setOpen("Approved")} data-testid="mgr-approve-btn"><Check className="h-4 w-4 mr-1" /> Approve</Button>
      <Dialog open={!!open} onOpenChange={() => setOpen(null)}>
        <DialogContent><DialogHeader><DialogTitle>{open} claim {exp.expense_no}?</DialogTitle></DialogHeader>
          <Textarea placeholder="Remarks (optional)" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(null)}>Cancel</Button>
            <Button onClick={decide} className={open === "Approved" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function HRDecideButtons({ exp, onChanged }) {
  const [remarks, setRemarks] = useState("");
  const [utr, setUtr] = useState("");
  const [open, setOpen] = useState(null);
  const decide = async () => {
    try {
      await api.put(`/expenses/${exp.id}/hr-decision`, { decision: open, remarks, utr_number: utr || null });
      toast.success(open === "Reimbursed" ? "Marked as reimbursed" : "Rejected");
      setOpen(null); onChanged();
    } catch (e) { toast.error(formatApiError(e)); }
  };
  return (
    <>
      <Button variant="outline" className="text-red-600 border-red-200" onClick={() => setOpen("Rejected")} data-testid="hr-reject-btn"><X className="h-4 w-4 mr-1" /> Reject</Button>
      <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={() => setOpen("Reimbursed")} data-testid="hr-reimburse-btn"><IndianRupee className="h-4 w-4 mr-1" /> Mark reimbursed</Button>
      <Dialog open={!!open} onOpenChange={() => setOpen(null)}>
        <DialogContent><DialogHeader><DialogTitle>{open === "Reimbursed" ? "Confirm reimbursement" : "Reject claim"} {exp.expense_no}</DialogTitle></DialogHeader>
          {open === "Reimbursed" && (
            <div><Label>UTR / Reference number</Label><Input value={utr} onChange={(e) => setUtr(e.target.value)} placeholder="e.g. HDFC240215XX1234" data-testid="hr-utr" /></div>
          )}
          <Textarea placeholder="Remarks (optional)" value={remarks} onChange={(e) => setRemarks(e.target.value)} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(null)}>Cancel</Button>
            <Button onClick={decide} className={open === "Reimbursed" ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
